/**
 * CustomRefinementList component is a customizable filter list that integrates with Algolia's InstantSearch library.
 * It allows users to refine product search results by selecting specific attribute values (facets) from a list of options.
 *
 * Features:
 * - Displays a list of items (e.g., attributes like color, size, etc.) with checkboxes for refining search results.
 * - Supports transforming the list items through the `transformItems` prop for custom formatting.
 * - Conditionally renders a "Show More" button to toggle additional facet options if the number of items exceeds the limit.
 * - Items are rendered with counts to indicate how many results are associated with each option.
 * - Supports pagination for the facet list via the `limit` and `showMoreLimit` props.
 *
 * @param {string} attribute - The facet attribute to be refined (e.g., "colour", "size").
 * @param {function} transformItems - Optional function to transform or filter facet items before rendering.
 * @param {boolean} showMore - Whether to show the "Show More" button to expand the list (default is true).
 * @param {number} limit - The maximum number of items to display initially (default is 10).
 * @param {number} showMoreLimit - The maximum number of items to display when expanded (default is 1000).
 * @returns {JSX.Element|null} - A refined list of checkboxes for the selected attribute, or null if no refinements are available.
 */

import { useRefinementList, UseRefinementListProps } from 'react-instantsearch-core';
import React, { useState } from 'react';
import { getHexCode } from 'helpers/utils/getHexcode';

type SortOption = 'count' | 'alpha' | 'hidden';

interface Props extends UseRefinementListProps {
  attribute: string;
  sortRemainingBy?: SortOption;
  limit?: number;
  showMoreLimit?: number;
  showFirstLimit?: number;
  facetOrdering?: any;
  handleTrackFacetClick?: (attribute: string, value: string) => void;
  children?: (content: React.ReactNode) => React.ReactNode;
}

function CustomRefinementList({
  attribute,
  sortRemainingBy = 'count',
  showMoreLimit = 1000,
  showFirstLimit = 10,
  facetOrdering = {},
  handleTrackFacetClick,
  children,
}: Props) {
  const {
    items: rawItems,
    refine,
    canRefine,
  } = useRefinementList({
    attribute,
    showMore: false,
    escapeFacetValues: true,
    showMoreLimit: 1000,
    limit: 100,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  if (!canRefine) return null;

  const items = rawItems?.map((item) => ({
    ...item,
    label: item?.label,
    originalLabel: item?.label,
    hex: getHexCode(item?.label),
  }));

  const hiddenValues: string[] = facetOrdering?.values?.[attribute]?.hide ?? [];
  const pinnedValues: string[] = facetOrdering?.values?.[attribute]?.order ?? [];
  const orderingSort: SortOption = facetOrdering?.values?.[attribute]?.sortRemainingBy ?? sortRemainingBy;

  const visibleItems = items?.filter((item) => !hiddenValues?.includes(item?.label));

  const pinnedItems = pinnedValues
    ?.map((pv: string) => visibleItems?.find((item) => item?.label === pv))
    ?.filter((item): item is (typeof visibleItems)[0] => !!item);

  let sortedRemaining: any[] = [];

  if (orderingSort != 'hidden') {
    const Items = visibleItems?.filter((item) => !pinnedValues?.includes(item?.label));

    sortedRemaining = [...Items]?.sort((a, b) => {
      return orderingSort === 'alpha' ? a?.label?.localeCompare(b?.label) : b?.count - a?.count;
    });
  }

  const finalItems = [...pinnedItems, ...sortedRemaining];
  const beforeDisplayItems = finalItems
    ?.map((item) => {
      const formatedLabel = item?.label?.split(' > ') || [];
      const displayLabel = formatedLabel?.length > 1 ? formatedLabel?.[1] : formatedLabel?.[0];
      const newDisplaylabel = displayLabel?.replaceAll('percentage', '%') || displayLabel;
      return {
        ...item,
        label: newDisplaylabel,
        originalLabel: item?.label,
        hex: getHexCode(displayLabel),
      };
    })
    ?.filter((item) => !item?.highlighted?.toLowerCase()?.includes('brands'))
    ?.filter((item) => !item?.highlighted?.toLowerCase()?.includes('recently viewed'));
  const displayedItems = isExpanded
    ? beforeDisplayItems?.slice(0, showMoreLimit)
    : beforeDisplayItems?.slice(0, showFirstLimit);

  const content = (
    <div>
      <ul className="space-y-2">
        {displayedItems?.map((item) => (
          <li key={`${attribute}-${item.label}`} className="flex w-full items-center">
            <label className="flex w-full items-center justify-between">
              <input
                type="checkbox"
                checked={item.isRefined}
                onChange={() => refine(item.value)}
                className="mr-8 mt-1 h-19 w-19 rounded-sm border border-gray-300 bg-white shadow-inner focus:outline-none"
                onClick={() => {
                  if (handleTrackFacetClick) {
                    handleTrackFacetClick(attribute, item?.label);
                  }
                }}
              />
              <span className="flex-grow text-16 hover:text-[#127E57]">{item.label}</span>
              <span className="text-16 text-gray-500 before:content-['('] after:content-[')']">{item.count}</span>
            </label>
          </li>
        ))}
      </ul>

      {beforeDisplayItems?.length > showFirstLimit && (
        <button
          onClick={() => setIsExpanded((prev) => !prev)}
          className="mt-10 block w-full text-center text-16 hover:text-[#127E57]"
        >
          {isExpanded ? '- Collapse' : '+ Show more'}
        </button>
      )}
    </div>
  );

  return children ? children(content) : content;
}

export default CustomRefinementList;
